import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@material-ui/core';
import { Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSForm, SGSFormDate, SGSFormInput, SGSFormLayout, SGSRadioCtr } from 'components';
import SGSInputCurrency from 'components/SGSCurrency';
import { useCustomStyles } from 'core/mui/customStyles';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { getSalaryMinimumS6202Rules } from 'features/arretTravail/models/businessRules/SalaryMinimumS6202Rules';
import { maintienInterruptionActivityS6202Options } from 'features/arretTravail/models/salaryIncompletePeriod';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useSalaryIncompletePeriodSchema } from './schema';
import { TableSalaireIncompletePeriod } from './table';

type Props = {
    store: any;
    onSubmit: SubmitHandler<InterruptionActiviyS6202Schema>;
    actions?: JSX.Element;
};

export type InterruptionActivityS6202 = {
    id: string;
    startDate: Date | null;
    endDate: Date | null;
    reasonCod: string | null;
    amount: string | null;
    cotisationDeducted: string | null;
};

type SalaryMinimumS6202 = {
    amount: string | null;
    contract: string | null;
    contractDate: Date | null;
};

type InterruptionActiviyS6202Schema = {
    interruptionActivityS6202: {
        interruptions: InterruptionActivityS6202[];
    };
    salaryMinimumS6202: SalaryMinimumS6202;
    maintienInterruptionActivityS6202: string | null;
};

const FormInterruptionActivityS6202: React.FC<Props> = ({ onSubmit, store, actions }) => {
    const customStyles = useCustomStyles();
    const { activeStep, setStepFailed } = useArretTravailFormContext();
    const [displayMaintien, setDisplayMaintien] = useState(false);

    const { hasLess18, hasSalaryMinimumContratS6202, displaySalaryMinimumS6202 } = getSalaryMinimumS6202Rules(store);

    const validationSchema: Yup.SchemaOf<InterruptionActiviyS6202Schema> = useSalaryIncompletePeriodSchema({ displaySalaryMinimumS6202, hasSalaryMinimumContratS6202, displayMaintien });

    const nbrMonth = 1;
    const initialSalaires = Array.from({ length: nbrMonth }, (_, index) => {
        return {
            id: `${index + 1}`,
            startDate: null,
            endDate: null,
            reasonCod: null,
            amount: '',
            cotisationDeducted: '',
        };
    });

    const methods = useForm<InterruptionActiviyS6202Schema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            ...store,
            interruptionActivityS6202: {
                interruptions: store?.interruptionActivityS6202?.interruptions.length ? store.interruptionActivityS6202.interruptions : initialSalaires,
            },
        },
    });

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = methods;

    useValidationEffects({ errors, activeStep, setStepFailed, control });

    const {
        fields: interruptions,
        append: appendSalary,
        remove: removeSalary,
    } = useFieldArray<InterruptionActiviyS6202Schema, 'interruptionActivityS6202.interruptions', 'id'>({
        control,
        name: 'interruptionActivityS6202.interruptions',
        keyName: 'id',
    });

    const handleNewSalaire = () => {
        appendSalary({
            id: `${interruptions.length + 1}`,
            startDate: null,
            endDate: null,
            reasonCod: null,
            amount: '',
            cotisationDeducted: '',
        });
    };

    useEffect(() => {
        if (interruptions.length === 0) handleNewSalaire();
    }, [interruptions]);

    const watchStartDate = watch('interruptionActivityS6202.interruptions.0.startDate');
    const watchEndDate = watch('interruptionActivityS6202.interruptions.0.endDate');
    const watchReasonCod = watch('interruptionActivityS6202.interruptions.0.reasonCod');
    const watchCotisationDeducted = watch('interruptionActivityS6202.interruptions.0.cotisationDeducted');
    const watchAmount = watch('interruptionActivityS6202.interruptions.0.amount');
    useEffect(() => {
        if (watchStartDate && watchEndDate && watchReasonCod && watchCotisationDeducted && watchAmount) {
            setDisplayMaintien(true); // TODO :: replace by setDisplayMaintien ??
        } else {
            setDisplayMaintien(false);
            setValue('maintienInterruptionActivityS6202', null); // TODO :: add validation
        }
    }, [watchStartDate, watchEndDate, watchReasonCod, watchCotisationDeducted, watchAmount]);

    console.log(errors);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center" style={{ marginBottom: '20px' }}>
                    <Grid item xs={12} md={12} className="mb-5">
                        <TableSalaireIncompletePeriod interruptions={interruptions} appendSalary={handleNewSalaire} removeSalary={removeSalary} />
                    </Grid>
                    {displayMaintien && displaySalaryMinimumS6202 && (
                        <>
                            <Grid item xs={0} md={3}></Grid>
                            <Grid item xs={12} md={6} className="mb-3">
                                <SGSForm.Row>
                                    <SGSForm.Col>
                                        <SGSRadioCtr
                                            row
                                            options={maintienInterruptionActivityS6202Options}
                                            name="maintienInterruptionActivityS6202"
                                            className="mb-n3"
                                            label="La victime a-t-elle bénéficié d'un maintien de salaire ?"
                                        />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                            </Grid>
                            <Grid item xs={0} md={3}></Grid>
                        </>
                    )}

                    {displaySalaryMinimumS6202 && (
                        <Grid item xs={12} md={6}>
                            <SGSForm.Row>
                                {hasLess18 && (
                                    <Box className={classNames(customStyles.txtBlueLight, 'mb-2')}>
                                        Salariés de moins de 18 ans : Indiquez le salaire minimum applicable au salarié adulte de la même catégorie.
                                    </Box>
                                )}
                                {!hasLess18 && (
                                    <Box className={classNames(customStyles.txtBlueLight, 'mb-2')}>
                                        Apprentis ou stagiaires : Précisez le salaire minimum de la catégorie, de l'échelon ou de l'emploi qualifié dans lequel l'apprenti ou le stagiaire aurait
                                        normalement été classé à la fin de l'apprentissage ou du stage.
                                    </Box>
                                )}
                                <SGSForm.Col md={6}>
                                    <SGSInputCurrency name="salaryMinimumS6202.amount" label="* Salaire minimum" />
                                </SGSForm.Col>
                            </SGSForm.Row>
                            {hasSalaryMinimumContratS6202 && (
                                <SGSForm.Row>
                                    <SGSForm.Col md={6}>
                                        <SGSFormInput variant="outlined" name="salaryMinimumS6202.contract" label="* N° du contrat" />
                                    </SGSForm.Col>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="salaryMinimumS6202.contractDate" label="*Date du contrat" />
                                    </SGSForm.Col>
                                </SGSForm.Row>
                            )}
                        </Grid>
                    )}
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormInterruptionActivityS6202;
